import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Chip, Select, MenuItem, InputLabel, Grid, FormControl, CircularProgress } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

import { JMC_COLORS } from "../../styles/colors";
import { get } from "../../pages/shared/api-service";

dayjs.extend(isoWeek);

export const STATUS_COLORS = {
    planned: '#1565C0',
    completed: '#2E7D32',
    cancelled: '#C62828',
    "in-production": '#FB8C00',
    "in transit": '#6A1B9A',
    delivered: '#00897B',
};



const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// ---------- Filter dispatch by week ----------
const filterDataBySelectedWeek = (data, selectedDate) => {
    const start = dayjs(selectedDate).isoWeekday(1);
    const end = start.add(6, "day");

    return data.filter((item) => {
        const d = dayjs(item.date);
        return d.isAfter(start.subtract(1, "day")) && d.isBefore(end.add(1, "day"));
    });
};

const normalizeItem = (o = {}) => {
    return {
        id: o.order_id || o.dispatch_id || o.id || "",
        qty:
            o.total_quantity_tons ||
            o.dispatch_quantity_tons ||
            o.quantity ||
            o.qty ||
            0,
        date:
            o.target_completion_date ||
            o.dispatch_date ||
            o.date ||
            o.created_at ||
            "",
        status: (o.status || "").toLowerCase(),
        type: o.dispatch_id ? "dispatch" : "order",

        // Return all fields if missing
        order_id: o.order_id || "",
        dispatch_id: o.dispatch_id || "",
        customer_name: o.customer_name || "",
        paper_grade: o.paper_grade || "",
        total_quantity_tons: o.total_quantity_tons || 0,
        target_completion_date: o.target_completion_date || "",

        client_name: o.client_name || "",
        dispatch_quantity_tons: o.dispatch_quantity_tons || 0,
        dispatch_date: o.dispatch_date || "",
        transport_company: o.transport_company || "",
    };
};

export default function ProductionWeeklyReport() {
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [weekData, setWeekData] = useState([]);
    const [filterStatus, setFilterStatus] = useState("all");
    const [loading, setLoading] = useState(false);

    // 👉 FIRST declare week range
    const weekStart = dayjs(selectedDate).isoWeekday(1);
    const weekEnd = weekStart.add(6, "day");

    const filteredWeekData = filterDataBySelectedWeek(weekData, selectedDate);

    // 👉 NOW group by days (AFTER weekStart defined)
    const groupedData = days.map((day, idx) => {
        const currentDate = dayjs(weekStart).add(idx, "day");

        const items = filteredWeekData.filter((d) => {
            const dayNumber = dayjs(d.date).day(); // 0 = Sunday
            if (dayNumber === idx + 1) return true; // Mon–Sat
            if (dayNumber === 0 && idx === 6) return true; // Sunday
            return false;
        });

        return {
            day,
            date: currentDate,
            formattedDate: currentDate.format("DD MMM"),
            items
        };
    });


    const getWeeklyReport = async () => {
        try {
            setLoading(true);
            const response = await get("/api/weekly-report", {
                from_date: weekStart.format("YYYY-MM-DD"),
                end_date: weekEnd.format("YYYY-MM-DD"),
                tab: filterStatus,
            });

            const orders = Array.isArray(response?.orders) ? response.orders : [];
            const dispatches = Array.isArray(response?.dispatches) ? response.dispatches : [];
            setWeekData([...orders, ...dispatches].map(normalizeItem));
        } catch (err) {
            console.error("getWeeklyReport", err);
            setWeekData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getWeeklyReport();
    }, [selectedDate, filterStatus]);

    return (
        <Box>
            {/* ---------------------- HEADER ---------------------- */}
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: JMC_COLORS.primary,
                    display: "flex",
                    alignItems: "center",
                }}
            >
                Weekly Dispatch / Order Calendar
            </Typography>

            {/* ---------------------- FILTERS ---------------------- */}
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 3 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Select Date"
                        value={selectedDate}
                        onChange={(newValue) => setSelectedDate(newValue)}
                        sx={{ width: 250 }}
                    />
                </LocalizationProvider>

                <Grid item xs={12} sm={6}>
                    <FormControl
                        fullWidth
                        size="small"
                        sx={{
                            minWidth: '160px',
                            '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                            '& .MuiInputBase-input': { fontSize: 14 },
                            '& .MuiInputLabel-root': { fontSize: 14 }
                        }}
                    >
                        <InputLabel id="status-filter-label">Status</InputLabel>
                        <Select
                            labelId="status-filter-label"
                            label="Status"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="order">Order</MenuItem>
                            <MenuItem value="dispatch">Dispatch</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Typography sx={{ fontSize: 14, color: JMC_COLORS.text.secondary }}>
                    Week: <b>{weekStart.format("DD MMM")} → {weekEnd.format("DD MMM YYYY")}</b>
                </Typography>
            </Box>

            {/* ---------------------- GRID ---------------------- */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 3 }}>
                    {/* Check if all days have no data */}
                    {groupedData.every(col => col.items.length === 0) ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography sx={{ color: JMC_COLORS.text.secondary }}>
                                No data available for the selected week
                            </Typography>
                        </Box>
                    ) : (
                        // Only show days that have data
                        groupedData
                            .filter(col => col.items.length > 0)
                            .map((col) => (
                                <Paper
                                    key={col.day}
                                    elevation={0}
                                    sx={{
                                        px: 2,
                                py: 2,
                                borderRadius: 3,
                                backgroundColor: "#F8FAFC", // light main bg
                                border: "1px solid #E2E8F0",
                            }}
                        >
                            {/* Day + Date */}
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: 15,
                                        color: JMC_COLORS.primary,
                                    }}
                                >
                                    {col.day}
                                    <span style={{ fontSize: 13, color: JMC_COLORS.text.disabled, paddingLeft: 8 }}>
                                        {col.formattedDate}
                                    </span>
                                </Typography>
                            </Box>

                            {/* SCROLLABLE CARD LIST */}
                            {
                                col.items.length > 0 && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: 2,
                                            overflowX: "auto",
                                            mt: 1.2,
                                            pb: 1,
                                            pr: 1,
                                            "&::-webkit-scrollbar": { height: 4 },
                                            "&::-webkit-scrollbar-thumb": {
                                                background: "#CBD5E1",
                                                borderRadius: 4,
                                            },
                                        }}
                                    >
                                        {col.items.map((item) => {
                                            const isOrder = item.type === "order";
                                            return (
                                                <Paper
                                                    key={item.id}
                                                    sx={{
                                                        minWidth: 220,
                                                        padding: 1.8,
                                                        borderRadius: 3,
                                                        border: "1px solid #CBD5E1",
                                                        background: isOrder ? "#fff" : "#fff",
                                                        boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
                                                        flexShrink: 0, // 🚀 important scroll fix
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: JMC_COLORS.primary }}>
                                                        #{item.id} ({item.type})
                                                    </Typography>

                                                    <Typography sx={{ fontSize: 12, color: JMC_COLORS.text.secondary }}>
                                                        {item.customer_name || item.client_name}
                                                    </Typography>

                                                    <Typography sx={{ fontSize: 12, color: JMC_COLORS.text.primary }}>
                                                        Qty: <b style={{ color: JMC_COLORS.secondary }}>{item.qty} MT</b>
                                                    </Typography>

                                                    <Chip
                                                        label={item.status.toUpperCase()}
                                                        size="small"
                                                        sx={{
                                                            mt: 0.8,
                                                            fontSize: 10,
                                                            fontWeight: 600,
                                                            color: "#fff",
                                                            backgroundColor: STATUS_COLORS[item.status] || JMC_COLORS.primary,
                                                        }}
                                                    />
                                                </Paper>
                                            );
                                        })}
                                    </Box>
                                )
                            }
                        </Paper>
                        )
                    ))}
                </Box>
            )
            }

        </Box >
    );
}

